import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { InteractiveFeature } from '@lp/shared';
import { CheckCircle2 } from 'lucide-react';
import { api } from '../../lib/api.js';
import { Alert, Badge, Button, Card, CardHeader, SectionHeading, Tabs } from '../ui.js';
import type { InteractiveEventResponse } from '../../lib/contracts.js';
import { HostedAppFrame } from './HostedAppFrame.js';
import { NotebookFrame } from './NotebookFrame.js';
import { NotebookViewer } from './NotebookViewer.js';
import { useEmbedEnvBridge } from './embedEnv.js';

/**
 * Embedded exercises.
 *
 * The iframe is sandboxed to scripts only: no same-origin, no forms, no
 * top-navigation, no popups. That means the embed cannot read this page, cannot
 * touch the session's cookies and cannot navigate the learner away from the
 * lecture — which is what makes it safe to let an author point one at a bundle.
 *
 * The bridge back is two words wide. A message is acted on only when it
 * comes from this card's own frame *and* its payload is exactly
 * `{ type: 'lp:event', event: 'viewed' | 'completed' }`. Everything else —
 * another window, extra keys, unknown event, a string instead of an object —
 * is dropped without a sound, because a noisy bridge is a bridge people start
 * probing.
 *
 * The sender is identified by `event.source`, not `event.origin`: a frame
 * sandboxed without allow-same-origin has an opaque origin, so every message
 * from an uploaded bundle arrives with origin "null" and an origin check
 * would drop them all. The source window is the frame we created, which no
 * other page can impersonate.
 *
 * An external embed on a host the operator lists in EMBED_TRUSTED_HOSTS also
 * gets allow-same-origin, so an app that needs storage or the Cache API (a
 * model loaded in the browser, say) works. Its origin is the third party's,
 * never ours — the API refuses to trust any host on this platform's own site —
 * so it still cannot read this page; it can only use its own storage. Nothing
 * else in the sandbox is relaxed.
 *
 * Hosted Python apps (Streamlit, Gradio, Dash) are opened per viewer through
 * a launch URL, and since they cannot easily post to the page, they also get
 * a "Mark as done" button.
 *
 * Notebooks get the same button. A runnable one opens in JupyterLite on its
 * own origin (Python runs in the learner's browser) and counts as viewed once
 * its frame loads; a read-only one is rendered here from the saved document.
 *
 * An uploaded app may also ask for the environment variables its author set
 * (API keys for an LLM call, say) with `{ type: 'lp:env-request' }`; see
 * `embedEnv.ts`. A notebook gets its variables in `os.environ` instead.
 */

/** The sandbox for an uploaded bundle or external embed; see above. */
export function embedSandbox(trusted: boolean): string {
  return trusted ? 'allow-scripts allow-same-origin' : 'allow-scripts';
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}
export function InteractiveSection({
  features,
  sessionQueryKey,
}: {
  features: InteractiveFeature[];
  /** Invalidated when an embed reports completion — this session, nothing else. */
  sessionQueryKey: readonly unknown[];
}) {
  const ordered = useMemo(
    () =>
      [...features]
        .filter((feature) => feature.enabled)
        .sort((a, b) => a.orderIndex - b.orderIndex),
    [features],
  );

  const [chosenId, setChosenId] = useState<string | null>(null);

  if (!ordered.length) return null;

  // A removed or disabled artifact falls back to the first one rather than
  // leaving no sub-tab selected.
  const activeId = ordered.some((f) => f.id === chosenId) ? chosenId! : ordered[0]!.id;
  const single = ordered.length === 1;

  return (
    <section aria-labelledby="session-interactive">
      <SectionHeading id="session-interactive">Interactive</SectionHeading>
      {single ? null : (
        <Tabs
          className="mb-4"
          variant="pill"
          idPrefix="interactive"
          label="Interactive artifacts"
          active={activeId}
          onChange={setChosenId}
          tabs={ordered.map((feature) => {
            const kind = TYPE_LABEL[feature.type];
            const title = feature.title.trim();
            return {
              id: feature.id,
              title: title ? `${kind}: ${title}` : kind,
              label: (
                <>
                  <span>{kind}</span>
                  {title ? (
                    <span className="max-w-[14rem] truncate font-normal text-ink-muted">
                      {title}
                    </span>
                  ) : null}
                  {feature.progress?.completed ? (
                    <CheckCircle2 size={13} className="text-ok shrink-0" aria-label="Completed" />
                  ) : null}
                </>
              ),
            };
          })}
        />
      )}
      {/* Every artifact stays mounted and is only hidden, so a running notebook
          or app keeps its state when the learner flips to another one. */}
      {ordered.map((feature) => (
        <div
          key={feature.id}
          {...(single
            ? {}
            : {
                role: 'tabpanel',
                id: `interactive-panel-${feature.id}`,
                'aria-labelledby': `interactive-tab-${feature.id}`,
                hidden: feature.id !== activeId,
              })}
        >
          <InteractiveCard feature={feature} sessionQueryKey={sessionQueryKey} />
        </div>
      ))}
    </section>
  );
}

/** The short name a sub-tab leads with, before the artifact's own title. */
const TYPE_LABEL: Record<InteractiveFeature['type'], string> = {
  embedded_app: 'App',
  external_embed: 'Embed',
  hosted_app: 'App',
  notebook: 'Notebook',
  flashcards: 'Flashcards',
  code_sandbox: 'Sandbox',
};

function InteractiveCard({
  feature,
  sessionQueryKey,
}: {
  feature: InteractiveFeature;
  sessionQueryKey: readonly unknown[];
}) {
  const queryClient = useQueryClient();
  const frameRef = useRef<HTMLIFrameElement>(null);
  const sentRef = useRef<Set<string>>(new Set());
  const [marking, setMarking] = useState(false);

  const send = useCallback(
    (name: 'viewed' | 'completed'): Promise<void> => {
      // Each event is worth sending once per mount; the server is idempotent
      // anyway, but an embed in a loop should not become a write loop.
      if (sentRef.current.has(name)) return Promise.resolve();
      sentRef.current.add(name);
      return api
        .post<InteractiveEventResponse>(
          `/interactive/${encodeURIComponent(feature.id)}/event`,
          { event: name },
        )
        .then(() => {
          if (name === 'completed') {
            void queryClient.invalidateQueries({ queryKey: sessionQueryKey });
          }
        })
        .catch(() => {
          // Exercise progress is a convenience, not a record; a failure here
          // must not interrupt what the learner is doing inside the embed.
          sentRef.current.delete(name);
        });
    },
    [feature.id, queryClient, sessionQueryKey],
  );

  useEffect(() => {
    const onMessage = (event: MessageEvent<unknown>): void => {
      if (!frameRef.current || event.source !== frameRef.current.contentWindow) return;

      const data = event.data;
      if (typeof data !== 'object' || data === null) return;
      const keys = Object.keys(data as Record<string, unknown>);
      if (keys.length !== 2) return;

      const payload = data as { type?: unknown; event?: unknown };
      if (payload.type !== 'lp:event') return;
      if (payload.event !== 'viewed' && payload.event !== 'completed') return;

      void send(payload.event);
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [send]);

  useEmbedEnvBridge(
    frameRef,
    feature.id,
    feature.type === 'embedded_app',
    !!feature.access?.hasEnv,
  );

  const hosted = feature.type === 'hosted_app';
  const notebook = feature.type === 'notebook';

  return (
    <Card>
      <CardHeader
        title={feature.title}
        description={feature.description || undefined}
        actions={
          feature.progress?.completed ? (
            <Badge tone="ok" icon={<CheckCircle2 size={12} aria-hidden />}>
              Completed
            </Badge>
          ) : (hosted || notebook) && feature.bundleReady ? (
            <Button
              size="sm"
              loading={marking}
              icon={<CheckCircle2 size={14} aria-hidden />}
              onClick={() => {
                setMarking(true);
                void send('completed').finally(() => setMarking(false));
              }}
            >
              Mark as done
            </Button>
          ) : feature.progress?.viewed ? (
            <Badge>Opened</Badge>
          ) : null
        }
      />
      <div className="p-4">
        {notebook ? (
          !feature.bundleReady ? (
            <Alert tone="warn" title="This notebook is not ready">
              The notebook has not been uploaded yet. It will appear here once it is.
            </Alert>
          ) : feature.embedUrl ? (
            <NotebookFrame
              ref={frameRef}
              featureId={feature.id}
              url={feature.embedUrl}
              hasEnv={!!feature.access?.hasEnv}
              title={feature.title}
              heightPx={feature.heightPx}
              onLoad={() => void send('viewed')}
            />
          ) : (
            <NotebookViewer featureId={feature.id} maxHeightPx={Math.max(feature.heightPx, 480)} />
          )
        ) : hosted ? (
          feature.bundleReady ? (
            <HostedAppFrame
              ref={frameRef}
              featureId={feature.id}
              title={feature.title}
              heightPx={feature.heightPx}
            />
          ) : (
            <Alert tone="warn" title="This app is not ready">
              The app is still being built. It will appear here once it is running.
            </Alert>
          )
        ) : feature.embedUrl ? (
          <>
            <iframe
              ref={frameRef}
              src={feature.embedUrl}
              title={feature.title || 'Interactive exercise'}
              sandbox={embedSandbox(feature.embedTrusted)}
              referrerPolicy="no-referrer"
              loading="lazy"
              style={{ height: `${feature.heightPx}px` }}
              className="w-full rounded-control border border-line bg-surface-2"
            />
            {feature.embedTrusted ? (
              <p className="mt-2 text-xs text-ink-subtle">
                External app hosted by {hostOf(feature.embedUrl)}.
              </p>
            ) : null}
          </>
        ) : (
          <Alert tone="warn" title="This exercise is not ready">
            {feature.bundleReady
              ? 'The exercise has no address configured yet.'
              : 'The exercise bundle is still being prepared. It will appear here once it is uploaded.'}
          </Alert>
        )}
      </div>
    </Card>
  );
}
